import { PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import { ContentFile, SearchResult, apiService } from '../../../services/api'
import { ContentAST } from '../../../transforms/ast'
import { logger } from '../../../utils/logger'

const MOBILE_PREVIEW_SNAP_POINTS = [0.22, 0.5, 0.82]
const MOBILE_PREVIEW_DEFAULT_RATIO = 0.5

const getSearchResultKey = (result: SearchResult) =>
  `content:${result.filename}:${result.blockId}:${result.blockMatchStart}:${result.annotationType ?? 'text'}:${(result.tagIds ?? []).slice().sort().join(',')}`

interface UseBookSearchControllerArgs {
  bookId?: string
  isMobile: boolean
  currentChapter: ContentFile | null
  currentContent: ContentAST | null
  onNavigateSearchResult: (
    filename: string,
    blockId: string,
    matchText: string,
    matchStart: number,
  ) => Promise<boolean | void>
  onPreviewContentSaved?: (filename: string, updatedFile: ContentFile) => void
}

export function useBookSearchController({
  bookId,
  isMobile,
  currentChapter,
  currentContent,
  onNavigateSearchResult,
  onPreviewContentSaved,
}: UseBookSearchControllerArgs) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchSearched, setSearchSearched] = useState(false)

  const [keyboardPreviewOpen, setKeyboardPreviewOpen] = useState(false)
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false)
  const [mobilePreviewHeightRatio, setMobilePreviewHeightRatio] = useState(MOBILE_PREVIEW_DEFAULT_RATIO)
  const [mobilePreviewDragging, setMobilePreviewDragging] = useState(false)

  const [selectedPreviewResult, setSelectedPreviewResult] = useState<SearchResult | null>(null)
  const [previewPopupOffset, setPreviewPopupOffset] = useState({ x: 0, y: -20 })
  const [previewPopupDragging, setPreviewPopupDragging] = useState(false)

  const [previewAst, setPreviewAst] = useState<ContentAST | null>(null)
  const [previewContentIdentity, setPreviewContentIdentity] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)

  const [previewDirty, setPreviewDirty] = useState(false)
  const [previewSaving, setPreviewSaving] = useState(false)
  const [previewRecentlySaved, setPreviewRecentlySaved] = useState(false)
  const recentlySavedTimerRef = useRef<number | null>(null)

  const previewCacheRef = useRef<Map<string, ContentFile>>(new Map())
  const previewPopupDragRef = useRef<{ pointerX: number; pointerY: number; startX: number; startY: number } | null>(
    null,
  )
  const mobilePreviewDragRef = useRef<{ pointerY: number; startRatio: number } | null>(null)

  useEffect(() => {
    let cancelled = false

    if (!bookId) {
      setSearchResults([])
      setSearchSearched(false)
      setSearchLoading(false)
      setSelectedPreviewResult(null)
      return
    }

    const trimmedQuery = searchQuery.trim()
    const hasText = trimmedQuery.length >= 2
    const hasTags = selectedTagIds.length > 0

    if (!hasText && !hasTags) {
      setSearchResults([])
      setSearchSearched(false)
      setSearchLoading(false)
      setSelectedPreviewResult(null)
      return
    }

    const run = async () => {
      setSearchLoading(true)
      try {
        const resp = await apiService.searchChapters(
          bookId,
          hasText ? trimmedQuery : undefined,
          hasTags ? selectedTagIds : undefined,
        )
        if (!cancelled) {
          setSearchResults(resp.data)
          setSearchSearched(true)
        }
      } catch {
        if (!cancelled) {
          setSearchResults([])
          setSearchSearched(true)
        }
      } finally {
        if (!cancelled) {
          setSearchLoading(false)
        }
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [bookId, searchQuery, selectedTagIds])

  const resetPreviewEditState = useCallback(() => {
    setPreviewDirty(false)
    setPreviewSaving(false)
    setPreviewRecentlySaved(false)
    if (recentlySavedTimerRef.current) {
      window.clearTimeout(recentlySavedTimerRef.current)
      recentlySavedTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      if (recentlySavedTimerRef.current) {
        window.clearTimeout(recentlySavedTimerRef.current)
      }
    }
  }, [])

  const confirmDiscardPreviewEdits = useCallback(() => {
    if (!previewDirty) {
      return true
    }
    return window.confirm('Discard unsaved changes to this chapter?')
  }, [previewDirty])

  const handleSearchClear = useCallback(() => {
    if (!confirmDiscardPreviewEdits()) {
      return
    }
    setSearchQuery('')
    setSelectedTagIds([])
    setSearchResults([])
    setSearchSearched(false)
    setSearchLoading(false)
    setKeyboardPreviewOpen(false)
    setMobilePreviewOpen(false)
  }, [confirmDiscardPreviewEdits])

  const handleTagsChange = useCallback((tagIds: string[]) => {
    setSelectedTagIds(tagIds)
    setKeyboardPreviewOpen(false)
    setMobilePreviewOpen(false)
  }, [])

  const getMatchingSearchResult = useCallback(
    (filename: string, blockId: string, matchStart: number) =>
      searchResults.find(
        (result) => result.filename === filename && result.blockId === blockId && result.blockMatchStart === matchStart,
      ) || null,
    [searchResults],
  )

  const handleMobilePreviewSelection = useCallback(
    (filename: string, blockId: string, _matchText: string, matchStart: number) => {
      const nextResult = getMatchingSearchResult(filename, blockId, matchStart)
      if (!nextResult) {
        return
      }

      setSelectedPreviewResult(nextResult)
      setMobilePreviewOpen(true)
      setMobilePreviewHeightRatio((previous) => (mobilePreviewOpen ? previous : MOBILE_PREVIEW_DEFAULT_RATIO))
    },
    [getMatchingSearchResult, mobilePreviewOpen],
  )

  const handleDesktopPreviewSelection = useCallback(
    (filename: string, blockId: string, _matchText: string, matchStart: number) => {
      const nextResult = getMatchingSearchResult(filename, blockId, matchStart)
      if (!nextResult) {
        return
      }

      const isAlreadySelected =
        selectedPreviewResult && getSearchResultKey(selectedPreviewResult) === getSearchResultKey(nextResult)

      // Re-clicking the preview icon of the selected result just keeps/reopens the preview —
      // opening the file in the main editor is a click on the row itself, or Enter.
      if (isAlreadySelected) {
        setKeyboardPreviewOpen(true)
        return
      }

      if (!confirmDiscardPreviewEdits()) {
        return
      }
      resetPreviewEditState()

      setSelectedPreviewResult(nextResult)
      setKeyboardPreviewOpen(true)
    },
    [getMatchingSearchResult, selectedPreviewResult, confirmDiscardPreviewEdits, resetPreviewEditState],
  )

  const handleEnterSearchSelection = useCallback(async () => {
    const result = selectedPreviewResult
    if (!result) {
      return
    }

    if (!confirmDiscardPreviewEdits()) {
      return
    }
    resetPreviewEditState()

    const matchText = result.snippet.slice(result.matchStart, result.matchEnd)
    setKeyboardPreviewOpen(false)
    setMobilePreviewOpen(false)
    await onNavigateSearchResult(result.filename, result.blockId, matchText, result.blockMatchStart)
  }, [onNavigateSearchResult, selectedPreviewResult, confirmDiscardPreviewEdits, resetPreviewEditState])

  const handleSearchNavigate = useCallback(
    async (filename: string, blockId: string, matchText: string, matchStart: number) => {
      if (!confirmDiscardPreviewEdits()) {
        return
      }
      resetPreviewEditState()
      setKeyboardPreviewOpen(false)
      setMobilePreviewOpen(false)
      await onNavigateSearchResult(filename, blockId, matchText, matchStart)
    },
    [onNavigateSearchResult, confirmDiscardPreviewEdits, resetPreviewEditState],
  )

  const handleStepSearchSelection = useCallback(
    (direction: -1 | 1) => {
      if (searchResults.length === 0) {
        return
      }

      if (!confirmDiscardPreviewEdits()) {
        return
      }
      resetPreviewEditState()

      setSelectedPreviewResult((previous) => {
        if (!previous) {
          return direction === 1 ? searchResults[0] : searchResults[searchResults.length - 1]
        }

        const currentIndex = searchResults.findIndex(
          (result) => getSearchResultKey(result) === getSearchResultKey(previous),
        )

        if (currentIndex === -1) {
          return direction === 1 ? searchResults[0] : searchResults[searchResults.length - 1]
        }

        const nextIndex = Math.max(0, Math.min(searchResults.length - 1, currentIndex + direction))
        return searchResults[nextIndex]
      })
    },
    [searchResults, confirmDiscardPreviewEdits, resetPreviewEditState],
  )

  const handleStepSidePaneSearchSelection = useCallback(
    (direction: -1 | 1) => {
      handleStepSearchSelection(direction)
      if (!isMobile) {
        setKeyboardPreviewOpen(true)
      }
    },
    [handleStepSearchSelection, isMobile],
  )

  const handlePreviewEditChange = useCallback(() => {
    setPreviewDirty(true)
    setPreviewRecentlySaved(false)
  }, [])

  // Save the edited preview back to its source file (the popup shows the whole file, so the
  // flushed AST replaces the file's authorContent as-is). Returns whether the save succeeded.
  const handlePreviewEditSave = useCallback(
    async (flushedAst: ContentAST): Promise<boolean> => {
      const result = selectedPreviewResult
      if (!result || !bookId) {
        return false
      }
      setPreviewSaving(true)
      try {
        const saveRes = await apiService.updateChapter(
          bookId,
          result.filename,
          { authorContent: flushedAst },
          { returnFull: true },
        )
        if (!saveRes.success || !saveRes.data) {
          toast.error('Failed to save chapter')
          return false
        }
        previewCacheRef.current.set(`content:${result.filename}`, saveRes.data)
        setPreviewDirty(false)
        setPreviewRecentlySaved(true)
        if (recentlySavedTimerRef.current) {
          window.clearTimeout(recentlySavedTimerRef.current)
        }
        recentlySavedTimerRef.current = window.setTimeout(() => {
          setPreviewRecentlySaved(false)
          recentlySavedTimerRef.current = null
        }, 1500)
        onPreviewContentSaved?.(result.filename, saveRes.data)
        return true
      } catch (err) {
        logger.error('Search preview save failed', err)
        toast.error('Failed to save chapter')
        return false
      } finally {
        setPreviewSaving(false)
      }
    },
    [bookId, selectedPreviewResult, onPreviewContentSaved],
  )

  /**
   * Close the desktop preview popup. With unsaved edits, offers to save first (reading the
   * current AST via `getCurrentAst`, which flushes the editor's pending debounced sync);
   * declining offers a discard confirmation, and cancelling that keeps the popup open.
   */
  const handleCloseDesktopPreview = useCallback(
    async (getCurrentAst?: () => ContentAST | null) => {
      if (previewDirty) {
        const currentAst = getCurrentAst?.() ?? null
        if (currentAst && window.confirm('Save changes before closing?')) {
          const saved = await handlePreviewEditSave(currentAst)
          if (!saved) {
            return
          }
        } else if (!window.confirm('Close without saving? Your changes will be lost.')) {
          return
        }
      }
      setKeyboardPreviewOpen(false)
    },
    [previewDirty, handlePreviewEditSave],
  )

  const handleMobilePreviewPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) {
        return
      }

      mobilePreviewDragRef.current = {
        pointerY: event.clientY,
        startRatio: mobilePreviewHeightRatio,
      }
      setMobilePreviewDragging(true)
      event.preventDefault()
    },
    [mobilePreviewHeightRatio],
  )

  const handlePreviewPopupPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) {
        return
      }

      previewPopupDragRef.current = {
        pointerX: event.clientX,
        pointerY: event.clientY,
        startX: previewPopupOffset.x,
        startY: previewPopupOffset.y,
      }
      setPreviewPopupDragging(true)
      event.preventDefault()
    },
    [previewPopupOffset.x, previewPopupOffset.y],
  )

  useEffect(() => {
    if (searchResults.length === 0) {
      setSelectedPreviewResult(null)
      setKeyboardPreviewOpen(false)
      setMobilePreviewOpen(false)
      return
    }

    setSelectedPreviewResult((previous) => {
      if (!previous) {
        return null
      }
      return searchResults.find((result) => getSearchResultKey(result) === getSearchResultKey(previous)) || null
    })
  }, [searchResults])

  useEffect(() => {
    if (!keyboardPreviewOpen && !mobilePreviewOpen) {
      setPreviewAst(null)
      setPreviewContentIdentity(null)
      setPreviewError(null)
      setPreviewLoading(false)
      resetPreviewEditState()
    }

    if (!keyboardPreviewOpen) {
      setPreviewPopupOffset({ x: 0, y: -20 })
      setPreviewPopupDragging(false)
      previewPopupDragRef.current = null
    }
  }, [keyboardPreviewOpen, mobilePreviewOpen, resetPreviewEditState])

  useEffect(() => {
    if (!mobilePreviewOpen) {
      setMobilePreviewHeightRatio(MOBILE_PREVIEW_DEFAULT_RATIO)
      setMobilePreviewDragging(false)
      mobilePreviewDragRef.current = null
    }
  }, [mobilePreviewOpen])

  useEffect(() => {
    if (!previewPopupDragging) {
      return
    }

    const handlePointerMove = (event: PointerEvent) => {
      const dragState = previewPopupDragRef.current
      if (!dragState) {
        return
      }

      setPreviewPopupOffset({
        x: dragState.startX + (event.clientX - dragState.pointerX),
        y: dragState.startY + (event.clientY - dragState.pointerY),
      })
    }

    const stopDragging = () => {
      previewPopupDragRef.current = null
      setPreviewPopupDragging(false)
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', stopDragging)
    window.addEventListener('pointercancel', stopDragging)

    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', stopDragging)
      window.removeEventListener('pointercancel', stopDragging)
    }
  }, [previewPopupDragging])

  useEffect(() => {
    if (!mobilePreviewDragging) {
      return
    }

    const handlePointerMove = (event: PointerEvent) => {
      const dragState = mobilePreviewDragRef.current
      if (!dragState) {
        return
      }

      const nextRatio = dragState.startRatio + (dragState.pointerY - event.clientY) / window.innerHeight
      setMobilePreviewHeightRatio(
        Math.max(MOBILE_PREVIEW_SNAP_POINTS[0], Math.min(MOBILE_PREVIEW_SNAP_POINTS[2], nextRatio)),
      )
    }

    const stopDragging = () => {
      setMobilePreviewHeightRatio((ratio) =>
        MOBILE_PREVIEW_SNAP_POINTS.reduce((closest, point) =>
          Math.abs(point - ratio) < Math.abs(closest - ratio) ? point : closest,
        ),
      )
      mobilePreviewDragRef.current = null
      setMobilePreviewDragging(false)
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', stopDragging)
    window.addEventListener('pointercancel', stopDragging)

    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', stopDragging)
      window.removeEventListener('pointercancel', stopDragging)
    }
  }, [mobilePreviewDragging])

  useEffect(() => {
    setKeyboardPreviewOpen(false)
    setMobilePreviewOpen(false)
  }, [searchQuery, selectedTagIds])

  useEffect(() => {
    let cancelled = false

    const loadPreview = async () => {
      if ((!keyboardPreviewOpen && !mobilePreviewOpen) || !selectedPreviewResult || !bookId) {
        return
      }

      setPreviewError(null)

      const cacheKey = `content:${selectedPreviewResult.filename}`
      const isCurrentChapterPreview = currentChapter?.filename === selectedPreviewResult.filename

      if (isCurrentChapterPreview && currentContent) {
        setPreviewAst(currentContent)
        setPreviewContentIdentity(cacheKey)
        setPreviewLoading(false)
        return
      }

      const cachedChapter = previewCacheRef.current.get(cacheKey)
      const cachedSource = cachedChapter?.authorContent
      if (cachedSource) {
        setPreviewAst(cachedSource)
        setPreviewContentIdentity(cacheKey)
        setPreviewLoading(false)
        return
      }

      setPreviewAst(null)
      setPreviewContentIdentity(null)
      setPreviewLoading(true)
      try {
        const fullChapter = await apiService.getChapter(bookId, selectedPreviewResult.filename)
        if (cancelled) {
          return
        }
        previewCacheRef.current.set(cacheKey, fullChapter.data)
        setPreviewAst(fullChapter.data.authorContent || null)
        setPreviewContentIdentity(cacheKey)
      } catch (err) {
        if (!cancelled) {
          logger.error('Failed to load search preview chapter:', err)
          setPreviewAst(null)
          setPreviewContentIdentity(null)
          setPreviewError('Failed to load preview content.')
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false)
        }
      }
    }

    loadPreview()

    return () => {
      cancelled = true
    }
  }, [bookId, currentChapter?.filename, currentContent, keyboardPreviewOpen, mobilePreviewOpen, selectedPreviewResult])

  const activePreviewIdentity = selectedPreviewResult ? `content:${selectedPreviewResult.filename}` : null
  const resolvedPreviewAst = activePreviewIdentity === previewContentIdentity ? previewAst : null
  const resolvedPreviewError = activePreviewIdentity === previewContentIdentity ? previewError : null
  const resolvedPreviewLoading =
    previewLoading ||
    ((keyboardPreviewOpen || mobilePreviewOpen) &&
      Boolean(activePreviewIdentity) &&
      activePreviewIdentity !== previewContentIdentity)

  const invalidatePreviewFile = useCallback(
    (filename: string) => {
      previewCacheRef.current.delete(`content:${filename}`)
      if (selectedPreviewResult?.filename === filename) {
        setKeyboardPreviewOpen(false)
        setMobilePreviewOpen(false)
        setPreviewAst(null)
        setPreviewContentIdentity(null)
      }
    },
    [selectedPreviewResult],
  )

  return {
    invalidatePreviewFile,
    searchQuery,
    selectedTagIds,
    searchResults,
    searchLoading,
    searchSearched,
    keyboardPreviewOpen,
    mobilePreviewOpen,
    mobilePreviewHeightRatio,
    mobilePreviewDragging,
    selectedPreviewResult,
    previewPopupOffset,
    previewPopupDragging,
    resolvedPreviewAst,
    resolvedPreviewError,
    resolvedPreviewLoading,
    previewDirty,
    previewSaving,
    previewRecentlySaved,
    setSearchQuery,
    setMobilePreviewOpen,
    handleSearchClear,
    handleCloseDesktopPreview,
    handlePreviewEditChange,
    handlePreviewEditSave,
    handleTagsChange,
    handleSearchNavigate,
    handleMobilePreviewSelection,
    handleDesktopPreviewSelection,
    handleEnterSearchSelection,
    handleStepSidePaneSearchSelection,
    handlePreviewPopupPointerDown,
    handleMobilePreviewPointerDown,
    getSearchResultKey,
  }
}
